import type { CmmdOrgProviderPolicy, ServerProvider } from "@cmmd-forge/contracts";

import type {
  ForgeProviderAccessState,
  ProviderCredentialMode,
} from "../../cmmd/forgeProviderAccess";
import { resolveShellProviderAvailabilityRows } from "../../cmmd/useShellProviderAvailability";
import type { ModelPickerProviderAvailability } from "../chat/modelPickerProviderAvailability";

/**
 * One availability per rail row for the draft composer.
 *
 * This replaces two functions that each reconstructed a reason from an absence:
 * one walked the live inventory and asked why the eligible filter had dropped a
 * provider, the other did the same for the provision-time plan. Both produced
 * entries only for providers something had already removed, so a provider that
 * was never published had no reason at all and the rail said "Coming soon."
 *
 * Every row gets an answer now, and the answer comes from the join rather than
 * from what is missing. See
 * `docs/adr/withheld-providers-carry-their-reason.md`.
 */
/**
 * The picker gets the Provider Inventory as published, not the eligible subset.
 *
 * Handing it a filtered list is what made a Withheld Provider vanish: the rail
 * had no row to put a reason on, so it fell through to "Coming soon." The
 * picker disables what a person may not select; it needs to see it first.
 *
 * `providers` is OMITTED rather than passed as `undefined`, which is how the
 * picker tells "not started yet" from "answered nothing".
 */
export function resolveNewThreadPickerProviderProps(input: {
  readonly inventoryKnown: boolean;
  readonly providers: ReadonlyArray<ServerProvider>;
}): { readonly providers?: ReadonlyArray<ServerProvider> } {
  return input.inventoryKnown ? { providers: input.providers } : {};
}

export function resolveDraftProviderAvailability(input: {
  readonly providers: ReadonlyArray<ServerProvider> | undefined;
  readonly policy: CmmdOrgProviderPolicy | null | undefined;
  readonly access: ForgeProviderAccessState;
  readonly credentialMode: ProviderCredentialMode;
}): ReadonlyArray<ModelPickerProviderAvailability> {
  // Delegates to the one producer so the draft composer and every other
  // Environment-scoped surface reach the same answer for the same inputs.
  // `providers` carries "no inventory yet" as `undefined` here; the shared
  // function wants that as an explicit `hasLiveInventory` flag instead.
  return resolveShellProviderAvailabilityRows({
    providers: input.providers ?? [],
    policy: input.policy,
    access: input.access,
    credentialMode: input.credentialMode,
    hasLiveInventory: input.providers !== undefined,
  });
}
